import React, { useState, useEffect, memo, useCallback, useId } from 'react';
import { Clock, Plus, Save, Trash2 } from 'lucide-react';
import { TimeEntry, Shift } from '../types/database';
import { formatDate, validateTimeFormat, calculateHours } from '../utils/dateUtils';
import { formatLongDate } from '../lib/format';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { ConfirmButton } from './ui/confirm-button';
import { ShiftEditor } from './ShiftEditor';

interface TimeEntryFormProps {
  selectedDate: Date;
  existingEntry?: TimeEntry | null;
  onSave: (entry: Omit<TimeEntry, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

type EditableShift = Omit<Shift, 'id' | 'created_at' | 'updated_at'>;

const emptyShift = (): EditableShift => ({ clockIn: '', clockOut: '', hours: 0 });

export const TimeEntryForm: React.FC<TimeEntryFormProps> = memo(({
  selectedDate,
  existingEntry,
  onSave,
  onDelete,
  isOpen,
  onClose
}) => {
  const formId = useId();
  const [shifts, setShifts] = useState<EditableShift[]>(existingEntry?.shifts || [emptyShift()]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset the form whenever it opens or the underlying entry changes.
  // New days start with one blank shift ready to fill in.
  useEffect(() => {
    if (!isOpen) return;
    setShifts(existingEntry?.shifts?.length ? existingEntry.shifts : [emptyShift()]);
    setErrors({});
  }, [existingEntry, isOpen]);

  const addShift = useCallback(() => {
    setShifts([...shifts, emptyShift()]);
  }, [shifts]);

  const removeShift = useCallback((index: number) => {
    if (shifts.length > 1) {
      setShifts(shifts.filter((_, i) => i !== index));
    }
  }, [shifts]);

  const updateShift = useCallback((index: number, field: 'clockIn' | 'clockOut', value: string) => {
    const newShifts = [...shifts];
    newShifts[index] = { ...newShifts[index], [field]: value };

    // Calculate hours for this shift
    if (newShifts[index].clockIn && newShifts[index].clockOut &&
        validateTimeFormat(newShifts[index].clockIn) && validateTimeFormat(newShifts[index].clockOut)) {
      const calculatedHours = calculateHours(newShifts[index].clockIn, newShifts[index].clockOut, selectedDate);
      newShifts[index].hours = calculatedHours;
    }

    setShifts(newShifts);
  }, [shifts, selectedDate]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    shifts.forEach((shift, index) => {
      if (!shift.clockIn) {
        newErrors[`shift${index}ClockIn`] = 'Required';
      } else if (!validateTimeFormat(shift.clockIn)) {
        newErrors[`shift${index}ClockIn`] = 'Use HH:MM';
      }

      if (!shift.clockOut) {
        newErrors[`shift${index}ClockOut`] = 'Required';
      } else if (!validateTimeFormat(shift.clockOut)) {
        newErrors[`shift${index}ClockOut`] = 'Use HH:MM';
      }

      if (shift.clockIn && shift.clockOut && validateTimeFormat(shift.clockIn) && validateTimeFormat(shift.clockOut)) {
        const calculatedHours = calculateHours(shift.clockIn, shift.clockOut, selectedDate);
        if (calculatedHours <= 0) {
          newErrors[`shift${index}ClockOut`] = 'Must be after clock in';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const totalHours = shifts.reduce((sum, shift) => sum + shift.hours, 0);
      await onSave({
        date: formatDate(selectedDate),
        shifts,
        totalHours
      });
      onClose();
    } catch (error) {
      console.error('Failed to save time entry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (existingEntry?.id && onDelete) {
      setIsSubmitting(true);
      try {
        await onDelete(existingEntry.id);
        onClose();
      } catch (error) {
        console.error('Failed to delete time entry:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const totalHours = shifts.reduce((sum, shift) => sum + shift.hours, 0);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      icon={<Clock />}
      title={existingEntry ? 'Edit hours' : 'Log hours'}
      description={formatLongDate(selectedDate)}
      footer={
        <>
          {existingEntry && onDelete && (
            <ConfirmButton size="md" onConfirm={handleDelete} disabled={isSubmitting} confirmLabel="Confirm delete">
              <Trash2 />
              Delete
            </ConfirmButton>
          )}
          <Button variant="secondary" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={isSubmitting}>
            <Save />
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-3">
        {shifts.map((shift, index) => (
          <ShiftEditor
            key={index}
            index={index}
            shift={shift}
            errors={{
              clockIn: errors[`shift${index}ClockIn`],
              clockOut: errors[`shift${index}ClockOut`],
            }}
            onChange={(field, value) => updateShift(index, field, value)}
            onRemove={shifts.length > 1 ? () => removeShift(index) : undefined}
          />
        ))}

        <button
          type="button"
          onClick={addShift}
          className="flex h-10 items-center justify-center gap-2 rounded-xl border border-dashed border-line-strong text-[13px] font-medium text-fg-muted transition-colors hover:border-brand/50 hover:bg-brand/[0.06] hover:text-fg"
        >
          <Plus className="size-4" />
          Add another shift
        </button>

        <div className="mt-1 flex items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3">
          <span className="text-[13px] text-fg-muted">Total for the day</span>
          <span className="numeric text-lg font-medium text-fg">{totalHours.toFixed(2)}h</span>
        </div>
      </form>
    </Dialog>
  );
});
